import React, { useEffect, useState, useRef } from "react";
import { logger } from "../utils/logger";
import { fetchWithTimeout, getApiBaseUrl } from "../utils/http";
import "../vaultMigration.css";
import WizardNavigationButtons from "./components/WizardNavigationButtons";
import DocumentsStep from "./steps/DocumentsStep";
import ProcessStep from "./steps/ProcessStep";
import VocabularyStep from "./steps/VocabularyStep";
import ComparisonStep from "./steps/ComparisonStep";
import DownloadStep from "./steps/DownloadStep";
import {
  toUiRow,
  toApiRow,
  toProcessUiRow,
  toProcessSourceRowFromDocument,
  toComparisonApiRow,
} from "../utils/mappers";
import { getFailureMessage, normalizeFailureMessage } from "../utils/apiErrors";
import { wizardSteps as steps, documentTypes } from "../utils/wizardMeta";
import { getVocabularyEnhancedRows } from "../utils/vocabularyRows";
import {
  getUniqueModules as getUniqueModulesFromMappings,
  getFilteredVeevaMappings as getFilteredVeevaMappingsFromState,
  buildManualClientMapping,
} from "../utils/workflows/mappingFilters";
import { isWizardStepValid } from "../utils/workflows/wizardValidation";
import {
  buildDocumentRenderBlocks,
  getTableCells,
  hasVisibleText,
} from "../utils/documentRender";
import {
  renderVocabularyDiffText,
  renderProcessDiffText,
  renderComparisonText,
} from "../utils/diffRenderers";
import { 
  FiArrowLeft, 
  FiArrowRight, 
  FiCheck, 
  FiLink, 
  FiFileText, 
  FiCpu, 
  FiBook, 
  FiColumns, 
  FiDownload, 
  FiUploadCloud, 
  FiPlus, 
  FiEdit3, 
  FiFile, 
  FiCheckCircle,
  FiLoader, 
  FiX, 
  FiSave, 
  FiRefreshCw,
  FiExternalLink,
  FiAlertCircle,
  FiChevronDown
} from "react-icons/fi";

function MigrationWizard({ onBack, onComplete }) {
  const API_BASE_URL = getApiBaseUrl();
  const [currentStep, setCurrentStep] = useState(0);
  const [vocabularySkipped, setVocabularySkipped] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const mappingFileInputRef = useRef(null);
  const transactionIdRef = useRef("");
  const creatingTransactionRef = useRef(null);
  const mappingInitializationDoneRef = useRef(false);
  const [transactionId, setTransactionId] = useState("");
  const [isMappingLoading, setIsMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState("");
  const [mappingImportSummary, setMappingImportSummary] = useState(null);
  const [mappingSaveHint, setMappingSaveHint] = useState("");
  const [apiInProgress, setApiInProgress] = useState(false);
  const [apiActionLabel, setApiActionLabel] = useState("");
  const [apiPopup, setApiPopup] = useState({ open: false, message: "" });
  const toLoaderLabel = (value) => String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[\-_]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
  const isApiAction = (label) => apiActionLabel === toLoaderLabel(label);
  const [processGeneration, setProcessGeneration] = useState({
    running: false,
    status: "idle",
    totalRows: 0,
    generatedRows: 0,
    pendingRows: 0,
    message: ""
  });
  const processGenerationPromiseRef = useRef(null);
  const [vocabularyGeneration, setVocabularyGeneration] = useState({
    running: false,
    status: "idle",
    totalRows: 0,
    generatedRows: 0,
    pendingRows: 0,
    message: ""
  });
  const vocabularyGenerationPromiseRef = useRef(null);

  const userName = (localStorage.getItem("profileName") || "Admin").trim() || "Admin";

  const runVaultApi = async (actionLabel, executor, showLoader = true) => {
    if (showLoader) {
      setApiInProgress(true);
      setApiActionLabel(toLoaderLabel(actionLabel));
    }

    try {
      return await executor();
    } catch (error) {
      logger.error("vault_api_action_failed", { actionLabel, error: String(error?.message || error) });
      const failureMessage = normalizeFailureMessage(error?.message || "Request failed");
      setMappingError(failureMessage);
      setApiPopup({ open: true, message: failureMessage });
      throw error;
    } finally {
      if (showLoader) {
        setApiInProgress(false);
        setApiActionLabel("");
      }
    }
  };

  const getVocabularyRowsForCurrentRules = () => getVocabularyEnhancedRows(processRows, vocabularyChanges);

  // Step 1: Client/Custom Mappings State (Editable - Table 1)
  const [clientMappings, setClientMappings] = useState([]);

  // Step 1: Veeva Standard Mappings (Read-only Reference - Table 2)
  const [veevaStandardMappings, setVeevaStandardMappings] = useState([]);

  const [newClientMapping, setNewClientMapping] = useState({ module: "", veevaCRM: "", vaultCRM: "" });
  const [activeTable, setActiveTable] = useState("client");
  const [filterModule, setFilterModule] = useState("all");

  // Step 2: Document Selection State
  const [selectedDocType, setSelectedDocType] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [documentRows, setDocumentRows] = useState([]);
  const [showDocumentContent, setShowDocumentContent] = useState(false);
  // Step 3: Process State

  // Step 5: Vocabulary Changes State
  const [vocabularyChanges, setVocabularyChanges] = useState([
    { id: 1, original: "HCP", replacement: "Healthcare Professional", applied: true },
    { id: 2, original: "Rep", replacement: "Sales Representative", applied: true },
    { id: 3, original: "Call", replacement: "Visit", applied: true },
    { id: 4, original: "Territory", replacement: "Region", applied: true }
  ]);

  // Step 6: Comparison State
  const [processRows, setProcessRows] = useState([]);
  const [vocabularyRows, setVocabularyRows] = useState([]);
  const [vocabularyGenerated, setVocabularyGenerated] = useState(false);
  const [comparisonRows, setComparisonRows] = useState([]);
  const [comparisonSavedRows, setComparisonSavedRows] = useState([]);
  const [comparisonSaved, setComparisonSaved] = useState(false);
  const [comparisonEditMode, setComparisonEditMode] = useState(false);

  // Step 7: Download State
  const [downloadReady, setDownloadReady] = useState(false);

  useEffect(() => {
    const grids = Array.from(document.querySelectorAll(".process-document-compare-grid"));
    if (grids.length === 0) {
      return;
    }
    const cleanupEntries = [];

    grids.forEach((grid) => {
      const syncGridDimensions = () => {
        const panels = Array.from(grid.querySelectorAll(".process-document-panel"));
        if (panels.length < 2) {
          return;
        }

        const leftPanel = panels[0];
        const rightPanel = panels[1];

        const clearInlineSizing = (panel) => {
          const clearTargets = panel.querySelectorAll(
            "[data-sync-key], .document-render-row.is-paragraph, .document-render-table tr, .document-render-table td"
          );
          clearTargets.forEach((node) => {
            node.style.height = "";
            node.style.minHeight = "";
            node.style.width = "";
            node.style.minWidth = "";
          });
        };

        clearInlineSizing(leftPanel);
        clearInlineSizing(rightPanel);

        const leftNodes = Array.from(leftPanel.querySelectorAll("[data-sync-key]"));
        const rightNodes = Array.from(rightPanel.querySelectorAll("[data-sync-key]"));

        const rightByKey = new Map();
        rightNodes.forEach((node) => {
          const key = node.getAttribute("data-sync-key");
          if (key) {
            rightByKey.set(key, node);
          }
        });

        leftNodes.forEach((leftNode) => {
          const key = leftNode.getAttribute("data-sync-key");
          if (!key) {
            return;
          }

          const rightNode = rightByKey.get(key);
          if (!rightNode) {
            return;
          }

          const leftHeight = leftNode.getBoundingClientRect().height;
          const rightHeight = rightNode.getBoundingClientRect().height;
          const targetHeight = Math.max(leftHeight, rightHeight);
          if (targetHeight > 0) {
            const targetHeightPx = `${targetHeight}px`;
            leftNode.style.height = targetHeightPx;
            rightNode.style.height = targetHeightPx;
          }

          if (leftNode.getAttribute("data-sync-kind") === "cell") {
            const leftWidth = leftNode.getBoundingClientRect().width;
            const rightWidth = rightNode.getBoundingClientRect().width;
            const targetWidth = Math.max(leftWidth, rightWidth);
            if (targetWidth > 0) {
              const targetWidthPx = `${targetWidth}px`;
              leftNode.style.minWidth = targetWidthPx;
              rightNode.style.minWidth = targetWidthPx;
            }
          }
        });

        const leftParagraphRows = Array.from(leftPanel.querySelectorAll(".document-render-row.is-paragraph"));
        const rightParagraphRows = Array.from(rightPanel.querySelectorAll(".document-render-row.is-paragraph"));
        const paragraphCount = Math.min(leftParagraphRows.length, rightParagraphRows.length);
        for (let index = 0; index < paragraphCount; index += 1) {
          const leftRow = leftParagraphRows[index];
          const rightRow = rightParagraphRows[index];
          const targetHeight = Math.max(
            leftRow.getBoundingClientRect().height,
            rightRow.getBoundingClientRect().height
          );
          if (targetHeight > 0) {
            const targetHeightPx = `${targetHeight}px`;
            leftRow.style.height = targetHeightPx;
            rightRow.style.height = targetHeightPx;
          }
        }

        const leftTables = Array.from(leftPanel.querySelectorAll(".document-render-table"));
        const rightTables = Array.from(rightPanel.querySelectorAll(".document-render-table"));
        const tableCount = Math.min(leftTables.length, rightTables.length);
        for (let tableIndex = 0; tableIndex < tableCount; tableIndex += 1) {
          const leftTable = leftTables[tableIndex];
          const rightTable = rightTables[tableIndex];
          const leftRows = Array.from(leftTable.querySelectorAll("tr"));
          const rightRows = Array.from(rightTable.querySelectorAll("tr"));
          const rowCount = Math.min(leftRows.length, rightRows.length);

          for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
            const leftRow = leftRows[rowIndex];
            const rightRow = rightRows[rowIndex];
            const leftCells = Array.from(leftRow.children);
            const rightCells = Array.from(rightRow.children);
            const cellCount = Math.min(leftCells.length, rightCells.length);

            for (let cellIndex = 0; cellIndex < cellCount; cellIndex += 1) {
              const leftCell = leftCells[cellIndex];
              const rightCell = rightCells[cellIndex];
              const targetWidth = Math.max(
                leftCell.getBoundingClientRect().width,
                rightCell.getBoundingClientRect().width
              );
              if (targetWidth > 0) {
                const targetWidthPx = `${targetWidth}px`;
                leftCell.style.minWidth = targetWidthPx;
                rightCell.style.minWidth = targetWidthPx;
              }
            }

            const targetHeight = Math.max(
              leftRow.getBoundingClientRect().height,
              rightRow.getBoundingClientRect().height
            );
            if (targetHeight > 0) {
              const targetHeightPx = `${targetHeight}px`;
              leftRow.style.height = targetHeightPx;
              rightRow.style.height = targetHeightPx;
              leftCells.forEach((cell) => {
                cell.style.minHeight = targetHeightPx;
              });
              rightCells.forEach((cell) => {
                cell.style.minHeight = targetHeightPx;
              });
            }
          }
        }
      };

      let animationFrameId = 0;
      let scrollAnimationFrameId = 0;
      const scheduleSync = () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(() => {
          syncGridDimensions();
        });
      };

      const scrollers = Array.from(grid.querySelectorAll(".document-render-table-wrap"));
      if (scrollers.length < 2) {
        scheduleSync();
        cleanupEntries.push(() => {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
        });
        return;
      }

      const handleScroll = (event) => {
        const source = event.currentTarget;
        if (source.dataset.syncing === "1") {
          source.dataset.syncing = "0";
          return;
        }

        const nextLeft = source.scrollLeft;
        if (scrollAnimationFrameId) {
          cancelAnimationFrame(scrollAnimationFrameId);
        }
        scrollAnimationFrameId = requestAnimationFrame(() => {
          scrollers.forEach((target) => {
            if (target === source) {
              return;
            }
            target.dataset.syncing = "1";
            target.scrollLeft = nextLeft;
          });
        });
      };

      const handleInput = () => {
        scheduleSync();
      };

      const handleWindowResize = () => {
        scheduleSync();
      };

      const mutationObserver = new MutationObserver(() => {
        scheduleSync();
      });
      mutationObserver.observe(grid, { childList: true, subtree: true, characterData: true });

      let resizeObserver = null;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          scheduleSync();
        });
        resizeObserver.observe(grid);
      }

      scrollers.forEach((scroller) => {
        scroller.addEventListener("scroll", handleScroll, { passive: true });
      });

      grid.addEventListener("input", handleInput, true);
      window.addEventListener("resize", handleWindowResize);

      scheduleSync();

      cleanupEntries.push(() => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        if (scrollAnimationFrameId) {
          cancelAnimationFrame(scrollAnimationFrameId);
        }
        scrollers.forEach((scroller) => {
          scroller.removeEventListener("scroll", handleScroll);
        });
        grid.removeEventListener("input", handleInput, true);
        window.removeEventListener("resize", handleWindowResize);
        mutationObserver.disconnect();
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      });
    });

    return () => {
      cleanupEntries.forEach((cleanup) => cleanup());
    };
  }, [
    currentStep,
    processRows.length,
    vocabularyRows.length,
    comparisonRows.length,
    comparisonEditMode,
  ]);

  useEffect(() => {
    if (transactionId) {
      transactionIdRef.current = transactionId;
    }
  }, [transactionId]);

  const ensureTransaction = async () => {
    if (transactionIdRef.current) {
      return transactionIdRef.current;
    }

    if (creatingTransactionRef.current) {
      return creatingTransactionRef.current;
    }

    creatingTransactionRef.current = (async () => {
      const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-name": userName
        }
      });
      if (!response.ok) {
        throw new Error(await getFailureMessage(response, "Failed to initialize transaction"));
      }

      const payload = await response.json();
      const nextTransactionId = payload.transaction_id;
      transactionIdRef.current = nextTransactionId;
      setTransactionId(nextTransactionId);
      return nextTransactionId;
    })();

    try {
      return await creatingTransactionRef.current;
    } finally {
      creatingTransactionRef.current = null;
    }
  };

  const loadMappingRows = async (activeTransactionId, options = {}) => {
    const silent = Boolean(options?.silent);
    const requestType = String(options?.requestType || "").trim();
    const queryParams = new URLSearchParams();
    if (silent) {
      queryParams.set("silent", "true");
    }
    if (requestType) {
      queryParams.set("request_type", requestType);
    }
    const querySuffix = queryParams.toString() ? `?${queryParams.toString()}` : "";

    const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/mapping/rows${querySuffix}`, {
      method: "GET",
      headers: {
        "x-user-name": userName
      }
    });
    if (!response.ok) {
      throw new Error(await getFailureMessage(response, "Failed to load mapping rows"));
    }
    const payload = await response.json();
    setClientMappings((payload.rows || []).map(toUiRow));
  };

  const loadVeevaContextRows = async (activeTransactionId) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/mapping/veeva-context`, {
      method: "GET",
      headers: {
        "x-user-name": userName
      }
    });
    if (!response.ok) {
      throw new Error(await getFailureMessage(response, "Failed to load Veeva context"));
    }
    const payload = await response.json();
    setVeevaStandardMappings(payload.rows || []);
  };

  const loadDocumentRows = async (activeTransactionId) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/documents/rows`, {
      method: "GET",
      headers: {
        "x-user-name": userName
      }
    });
    if (!response.ok) {
      throw new Error(await getFailureMessage(response, "Failed to load document rows"));
    }
    const payload = await response.json();
    const rows = payload.rows || [];
    setDocumentRows(rows);
    return rows;
  };

  const loadProcessRows = async (activeTransactionId) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/process/rows`, {
      method: "GET",
      headers: {
        "x-user-name": userName
      }
    });
    if (!response.ok) {
      throw new Error(await getFailureMessage(response, "Failed to load process rows"));
    }
    const payload = await response.json();
    const rows = (payload.rows || []).map(toProcessUiRow);
    setProcessRows(rows);

    const totalRowsRaw = Number(payload.total_rows || 0);
    const generatedRowsRaw = Number(payload.generated_rows || rows.length || 0);
    const normalizedTotal = totalRowsRaw > 0 ? totalRowsRaw : Math.max(rows.length, generatedRowsRaw, 0);
    const normalizedGenerated = Math.min(Math.max(generatedRowsRaw, rows.length), normalizedTotal || Math.max(generatedRowsRaw, rows.length, 0));
    const normalizedPending = normalizedTotal > 0 ? Math.max(normalizedTotal - normalizedGenerated, 0) : 0;
    const status = String(payload.process_status || "").toLowerCase() || (rows.length > 0 ? "completed" : "idle");
    const derivedRunning = status === "running" || (normalizedTotal > 0 && normalizedGenerated < normalizedTotal);

    setProcessGeneration({
      running: derivedRunning,
      status,
      totalRows: normalizedTotal,
      generatedRows: normalizedGenerated,
      pendingRows: normalizedPending,
      message: String(payload.message || "")
    });

    if (rows.length > 0) {
      setComparisonRows(rows);
      setComparisonSavedRows(rows);
      setComparisonSaved(true);
      setComparisonEditMode(false);
    }

    const selectedNames = Array.isArray(payload.selected_modules) ? payload.selected_modules : [];
    if (selectedNames.length > 0) {
      // backend-selected module context is already reflected in generated rows
    }

    return payload;
  };

  const loadVocabularyRows = async (activeTransactionId) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/vocabulary/rows`, {
      method: "GET",
      headers: {
        "x-user-name": userName
      }
    });
    if (!response.ok) {
      return;
    }
    const payload = await response.json();
    const rows = (payload.rows || []).map(toProcessUiRow);
    setVocabularyRows(rows);

    const totalRowsRaw = Number(payload.total_rows || 0);
    const generatedRowsRaw = Number(payload.generated_rows || rows.length || 0);
    const normalizedTotal = totalRowsRaw > 0 ? totalRowsRaw : Math.max(rows.length, generatedRowsRaw, 0);
    const normalizedGenerated = Math.min(Math.max(generatedRowsRaw, rows.length), normalizedTotal || Math.max(generatedRowsRaw, rows.length, 0));
    const normalizedPending = normalizedTotal > 0 ? Math.max(normalizedTotal - normalizedGenerated, 0) : 0;
    const status = String(payload.process_status || "").toLowerCase() || (rows.length > 0 ? "completed" : "idle");
    const derivedRunning = status === "running" || (normalizedTotal > 0 && normalizedGenerated < normalizedTotal);

    setVocabularyGeneration({
      running: derivedRunning,
      status,
      totalRows: normalizedTotal,
      generatedRows: normalizedGenerated,
      pendingRows: normalizedPending,
      message: String(payload.message || "")
    });

    if (rows.length > 0 && !derivedRunning) {
      setVocabularyGenerated(true);
      setComparisonRows(rows);
      setComparisonSavedRows(rows);
      setComparisonSaved(true);
      setComparisonEditMode(false);
    } else {
      setVocabularyGenerated(false);
    }

    const processRowCount = processRows.length || processDisplayRowsOrdered.length || 0;
    if (!derivedRunning && rows.length === 0 && processRowCount > 0) {
      startVocabularyGeneration(activeTransactionId, processRowCount).catch(() => {});
    } else if (derivedRunning) {
      startVocabularyGeneration(activeTransactionId, normalizedTotal).catch(() => {});
    }
  };

  const loadComparisonRows = async (activeTransactionId) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/comparison/rows`, {
      method: "GET",
      headers: {
        "x-user-name": userName
      }
    });
    if (!response.ok) {
      return;
    }
    const payload = await response.json();
    const rows = (payload.rows || []).map(toProcessUiRow);
    if (rows.length > 0) {
      setComparisonRows(rows);
      setComparisonSavedRows(rows);
      setComparisonSaved(true);
      setComparisonEditMode(false);
    }
  };

  const startProcessGeneration = async (activeTransactionId, expectedTotalRows = 0) => {
    if (processGenerationPromiseRef.current) {
      return processGenerationPromiseRef.current;
    }

    setProcessGeneration({
      running: true,
      status: "running",
      totalRows: expectedTotalRows,
      generatedRows: 0,
      pendingRows: expectedTotalRows > 0 ? expectedTotalRows : 0,
      message: expectedTotalRows > 0
        ? `Generating process rows (0/${expectedTotalRows})`
        : "Generating process rows"
    });

    const promise = (async () => {
      let cancelled = false;
      let pollTimer = null;

      // Apply a process-stage snapshot (partial during generation, or final)
      // to the progress bar and comparison grid.
      const applyProcessSnapshot = (payload, { forceCompleted = false } = {}) => {
        const rows = (payload.rows || []).map(toProcessUiRow);
        if (rows.length > 0) {
          setProcessRows(rows);
          setComparisonRows(rows);
          setComparisonSavedRows(rows);
          setComparisonSaved(true);
          setComparisonEditMode(false);
        }

        const totalRows = Number(payload.total_rows || rows.length || expectedTotalRows || 0);
        const generatedRows = Number(payload.generated_rows || rows.length || 0);
        const normalizedGenerated = Math.min(Math.max(generatedRows, rows.length), totalRows || Math.max(generatedRows, rows.length, 0));
        const rawStatus = String(payload.process_status || "").toLowerCase();
        const pendingRows = forceCompleted ? 0 : Math.max(totalRows - normalizedGenerated, 0);
        const running = forceCompleted ? false : (rawStatus === "running" || pendingRows > 0);
        const status = forceCompleted ? (rawStatus || "completed") : (rawStatus || (pendingRows > 0 ? "running" : "completed"));

        setProcessGeneration({
          running,
          status,
          totalRows,
          generatedRows: forceCompleted && totalRows > 0 ? totalRows : normalizedGenerated,
          pendingRows,
          message: String(payload.message || (pendingRows > 0 ? "Generating process rows" : "Process comparison generated successfully"))
        });
      };

      // While the bulk request runs, poll the persisted process rows so the
      // progress bar advances (the backend writes progress row-by-row).
      const pollProgress = async () => {
        if (cancelled) {
          return;
        }
        try {
          const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/process/rows`, {
            method: "GET",
            headers: {
              "x-user-name": userName
            }
          }, 30000);
          if (cancelled || !response.ok) {
            return;
          }
          const payload = await response.json();
          if (cancelled) {
            return;
          }
          applyProcessSnapshot(payload);
        } catch {
          // Transient poll failure — ignore; the next tick retries.
        }
      };

      try {
        // Clear any stale vocabulary state, matching the previous behaviour.
        setVocabularyGenerated(false);
        setVocabularyRows([]);
        setVocabularyGeneration({
          running: false,
          status: "idle",
          totalRows: 0,
          generatedRows: 0,
          pendingRows: 0,
          message: ""
        });

        pollTimer = setInterval(pollProgress, 2500);

        const requestGenerate = async (attempt = 1) => {
          try {
            return await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/process/generate`, {
              method: "POST",
              headers: {
                "x-user-name": userName
              }
            }, 360000);
          } catch (error) {
            const message = String(error?.message || "").toLowerCase();
            const timedOut = message.includes("timed out") || message.includes("aborted");
            if (timedOut && attempt < 2) {
              return requestGenerate(attempt + 1);
            }
            throw error;
          }
        };

        const response = await requestGenerate();

        cancelled = true;
        if (pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }

        if (!response.ok) {
          throw new Error(await getFailureMessage(response, "Failed to generate process comparison"));
        }

        const payload = await response.json();
        applyProcessSnapshot(payload, { forceCompleted: true });
      } catch (error) {
        const message = normalizeFailureMessage(error?.message || "Failed to generate process comparison");
        setMappingError(message);
        setApiPopup({ open: true, message });
        setProcessGeneration((prev) => ({
          ...prev,
          running: false,
          status: "failed",
          message
        }));
      } finally {
        cancelled = true;
        if (pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
        processGenerationPromiseRef.current = null;
      }
    })();

    processGenerationPromiseRef.current = promise;
    return promise;
  };

  const startVocabularyGeneration = async (activeTransactionId, expectedTotalRows = 0) => {
    if (vocabularyGenerationPromiseRef.current) {
      return vocabularyGenerationPromiseRef.current;
    }

    setVocabularyGeneration({
      running: true,
      status: "running",
      totalRows: expectedTotalRows,
      generatedRows: 0,
      pendingRows: expectedTotalRows > 0 ? expectedTotalRows : 0,
      message: expectedTotalRows > 0
        ? `Generating vocabulary rows (0/${expectedTotalRows})`
        : "Generating vocabulary rows"
    });
    setVocabularyGenerated(false);

    const promise = (async () => {
      try {
        let keepGenerating = true;
        while (keepGenerating) {
          const requestGenerateNext = async (attempt = 1) => {
            try {
              return await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/vocabulary/generate-next`, {
                method: "POST",
                headers: {
                  "x-user-name": userName
                }
              }, 360000);
            } catch (error) {
              const message = String(error?.message || "").toLowerCase();
              const timedOut = message.includes("timed out") || message.includes("aborted");
              if (timedOut && attempt < 2) {
                return requestGenerateNext(attempt + 1);
              }
              throw error;
            }
          };

          const response = await requestGenerateNext();

          if (!response.ok) {
            throw new Error(await getFailureMessage(response, "Failed to generate next vocabulary row"));
          }

          const payload = await response.json();
          const rows = (payload.rows || []).map(toProcessUiRow);
          setVocabularyRows(rows);

          const totalRows = Number(payload.total_rows || rows.length || expectedTotalRows || 0);
          const generatedRows = Number(payload.generated_rows || rows.length || 0);
          const normalizedGenerated = Math.min(Math.max(generatedRows, rows.length), totalRows || Math.max(generatedRows, rows.length, 0));
          const status = String(payload.process_status || "").toLowerCase();
          const pendingRows = Math.max(totalRows - normalizedGenerated, 0);
          const running = status === "running" || pendingRows > 0;

          setVocabularyGeneration({
            running,
            status: status || (pendingRows > 0 ? "running" : "completed"),
            totalRows,
            generatedRows: normalizedGenerated,
            pendingRows,
            message: String(payload.message || (pendingRows > 0 ? "Generating vocabulary rows" : "Vocabulary enhancement generated successfully"))
          });

          if (!running && rows.length > 0) {
            setVocabularyGenerated(true);
            setComparisonRows(rows);
            setComparisonSavedRows(rows);
            setComparisonSaved(true);
            setComparisonEditMode(false);
          }

          keepGenerating = running;
        }
      } catch (error) {
        const message = normalizeFailureMessage(error?.message || "Failed to generate vocabulary enhancement");
        setMappingError(message);
        setApiPopup({ open: true, message });
        setVocabularyGenerated(false);
        setVocabularyGeneration((prev) => ({
          ...prev,
          running: false,
          status: "failed",
          message
        }));
      } finally {
        vocabularyGenerationPromiseRef.current = null;
      }
    })();

    vocabularyGenerationPromiseRef.current = promise;
    return promise;
  };

  const refreshMappingTable = async (activeTransactionId, options = {}) => {
    await loadMappingRows(activeTransactionId, options);
  };

  const handleRefreshMappingTable = async () => {
    setMappingError("");
    try {
      await runVaultApi("Refreshing mappings", async () => {
        const activeTransactionId = await ensureTransaction();
        await refreshMappingTable(activeTransactionId, { requestType: "refresh" });
      });
    } catch {
      return;
    }
  };

  useEffect(() => {
    if (mappingInitializationDoneRef.current) {
      return;
    }
    mappingInitializationDoneRef.current = true;

    const initialize = async () => {
      setIsMappingLoading(true);
      setMappingError("");
      try {
        await runVaultApi("Loading Mappings", async () => {
          const activeTransactionId = await ensureTransaction();
          await Promise.all([
            loadMappingRows(activeTransactionId),
            loadVeevaContextRows(activeTransactionId),
          ]);
        });
      } catch (error) {
        return;
      } finally {
        setIsMappingLoading(false);
      }
    };

    initialize();
    return undefined;
  }, []);

  const getUniqueModules = () => getUniqueModulesFromMappings(veevaStandardMappings);

  const getFilteredVeevaMappings = () => getFilteredVeevaMappingsFromState(veevaStandardMappings, filterModule);

  const getFilteredClientMappings = () => clientMappings;

  const handleAddClientMapping = () => {
    if (newClientMapping.module && newClientMapping.veevaCRM && newClientMapping.vaultCRM) {
      const manualMapping = buildManualClientMapping(newClientMapping);
      setClientMappings([...clientMappings, manualMapping]);
      setNewClientMapping({ module: "", veevaCRM: "", vaultCRM: "" });
      setMappingSaveHint("New mapping added. Click Save Mappings to persist it.");
    }
  };

  const handleSaveClientMappings = async () => {
    setMappingError("");
    try {
      await runVaultApi("Saving mappings", async () => {
        const activeTransactionId = await ensureTransaction();
        const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/mapping/rows`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-user-name": userName
          },
          body: JSON.stringify({ rows: clientMappings.map((row) => toApiRow(row, userName)) })
        });
        if (!response.ok) {
          throw new Error(await getFailureMessage(response, "Failed to save mapping rows"));
        }
        const payload = await response.json();
        setClientMappings((payload.rows || []).map(toUiRow));
        setMappingSaveHint("");
      });
    } catch (error) {
      return;
    }
  };

  const handleMappingFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setMappingError("");
      setMappingImportSummary(null);
      setIsMappingLoading(true);
      try {
        await runVaultApi("Importing mappings", async () => {
          const activeTransactionId = await ensureTransaction();
          const formData = new FormData();
          formData.append("file", file);

          const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/mapping/import`, {
            method: "POST",
            headers: {
              "x-user-name": userName
            },
            body: formData
          }, 90000);

          if (!response.ok) {
            throw new Error(await getFailureMessage(response, "Mapping import failed"));
          }

          const payload = await response.json();
          setMappingImportSummary(payload);
          await refreshMappingTable(activeTransactionId, { silent: true });
        });
      } catch (error) {
        return;
      } finally {
        setIsMappingLoading(false);
      }
    }
    if (mappingFileInputRef.current) {
      mappingFileInputRef.current.value = "";
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (!selectedDocType) {
      setMappingError("Please select Document Type before uploading a file");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setMappingError("");
    setDocumentRows([]);
    setShowDocumentContent(false);
    try {
      await runVaultApi("Uploading document", async () => {
        const activeTransactionId = await ensureTransaction();
        const formData = new FormData();
        formData.append("document_type", selectedDocType);
        formData.append("file", file);

        const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/documents/import`, {
          method: "POST",
          headers: {
            "x-user-name": userName
          },
          body: formData
        }, 120000);

        if (!response.ok) {
          throw new Error(await getFailureMessage(response, "Document upload failed"));
        }

        const payload = await response.json();
        setUploadedFiles((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            name: payload.source_file || file.name,
            size: file.size,
            type: file.type,
          }
        ]);
        setDocumentRows(payload.rows || []);
        setShowDocumentContent((payload.rows || []).length > 0);
        setVocabularyGenerated(false);
      });
    } catch (error) {
      return;
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRefreshDocumentContent = async () => {
    setMappingError("");
    try {
      await runVaultApi("Refreshing document content", async () => {
        const activeTransactionId = await ensureTransaction();
        const rows = await loadDocumentRows(activeTransactionId);
        setShowDocumentContent(rows.length > 0);
      });
    } catch {
      return;
    }
  };

  const handleClearDocumentContent = async () => {
    setMappingError("");
    try {
      await runVaultApi("Clearing document content", async () => {
        const activeTransactionId = await ensureTransaction();
        const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/documents`, {
          method: "DELETE",
          headers: {
            "x-user-name": userName
          }
        });
        if (!response.ok) {
          throw new Error(await getFailureMessage(response, "Failed to clear document content"));
        }

        setUploadedFiles([]);
        setDocumentRows([]);
        setShowDocumentContent(false);
        setVocabularyGenerated(false);
      });
    } catch {
      return;
    }
  };

  const handleRemoveFile = (id) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== id));
  };

  const handleApplyVocabulary = (id) => {
    setVocabularyChanges(vocabularyChanges.map(v => 
      v.id === id ? { ...v, applied: !v.applied } : v
    ));
  };

  const handleApplyAllVocabulary = () => {
    setVocabularyChanges(vocabularyChanges.map(v => ({ ...v, applied: true })));
  };

  const handleComparisonTextChange = (rowId, value) => {
    if (!comparisonEditMode) {
      return;
    }
    setComparisonSaved(false);
    setComparisonRows((prevRows) => prevRows.map((row) => {
      if (row.id !== rowId) {
        return row;
      }
      if (row.rowKind === "table") {
        return row;
      }
      return {
        ...row,
        finalStatement: value,
        finalCells: [value],
        edited: true
      };
    }));
  };

  const handleComparisonCellChange = (rowId, cellIndex, value) => {
    if (!comparisonEditMode) {
      return;
    }
    setComparisonSaved(false);
    setComparisonRows((prevRows) => prevRows.map((row) => {
      if (row.id !== rowId || row.rowKind !== "table") {
        return row;
      }
      const nextCells = [...(Array.isArray(row.finalCells) ? row.finalCells : [])];
      nextCells[cellIndex] = value;
      return {
        ...row,
        finalCells: nextCells,
        finalStatement: nextCells.join(" | "),
        edited: true
      };
    }));
  };

  const saveComparisonRows = async (activeTransactionId, rowsToSave) => {
    const payloadRows = rowsToSave.map(toComparisonApiRow);
    const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/comparison`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-user-name": userName
      },
      body: JSON.stringify({
        data: {
          rows: payloadRows,
          message: "Comparison rows saved"
        }
      })
    }, 120000);
    if (!response.ok) {
      throw new Error(await getFailureMessage(response, "Failed to save comparison rows"));
    }
    await response.json();
  };

  const prepareDownloadArtifact = async (activeTransactionId, rowsToSave) => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/prepare-download`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-user-name": userName
      },
      body: JSON.stringify({
        data: {
          rows: rowsToSave.map(toComparisonApiRow)
        }
      })
    }, 180000);
    if (!response.ok) {
      throw new Error(await getFailureMessage(response, "Failed to prepare download"));
    }
    return await response.json();
  };

  const handleSaveComparisonEdits = async () => {
    try {
      await runVaultApi("Saving comparison edits", async () => {
        const activeTransactionId = await ensureTransaction();
        const rowsToSave = [...comparisonRows].sort((a, b) => (a.rowNumber || 0) - (b.rowNumber || 0));
        await saveComparisonRows(activeTransactionId, rowsToSave);
        setComparisonSavedRows(rowsToSave);
        setComparisonSaved(true);
        setComparisonEditMode(false);
      });
    } catch {
      return;
    }
  };

  const handlePrepareDownload = async () => {
    setIsProcessing(true);
    try {
      await runVaultApi("Preparing download artifact", async () => {
        const activeTransactionId = await ensureTransaction();
        const rowsToSave = [...comparisonRows].sort((a, b) => (a.rowNumber || 0) - (b.rowNumber || 0));

        if (rowsToSave.length === 0) {
          throw new Error("No comparison content found. Complete Compare and Review first.");
        }

        await saveComparisonRows(activeTransactionId, rowsToSave);
        setComparisonSavedRows(rowsToSave);
        setComparisonSaved(true);
        setComparisonEditMode(false);

        const preparePayload = await prepareDownloadArtifact(activeTransactionId, rowsToSave);
        const isReady = preparePayload?.download_ready === true;
        setDownloadReady(isReady);

        if (!isReady) {
          const prepareMessage = String(preparePayload?.message || "Failed to prepare download artifact").trim();
          throw new Error(prepareMessage || "Failed to prepare download artifact");
        }
      });
    } catch {
      return;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    try {
      await runVaultApi("Downloading document", async () => {
        const activeTransactionId = await ensureTransaction();
        const response = await fetchWithTimeout(`${API_BASE_URL}/vault-migration/transactions/${activeTransactionId}/download`, {
          method: "GET",
          headers: {
            "x-user-name": userName
          }
        }, 180000);
        if (!response.ok) {
          throw new Error(await getFailureMessage(response, "Failed to download file"));
        }

        const blob = await response.blob();
        const disposition = response.headers.get("Content-Disposition") || "";
        const fileNameMatch = disposition.match(/filename="?([^\"]+)"?/i);
        const fileName = fileNameMatch?.[1] || (uploadedFiles.length > 0 ? `migrated_${uploadedFiles[0].name}` : "migrated_document.txt");
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    } catch {
      return;
    }
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      if (currentStep === 1) {
        try {
          const activeTransactionId = await ensureTransaction();
          const totalRows = Array.isArray(documentRows) ? documentRows.length : 0;

          setVocabularyGenerated(false);
          setProcessRows([]);
          setVocabularyRows([]);
          setComparisonRows([]);
          setComparisonSavedRows([]);
          setComparisonSaved(false);
          setComparisonEditMode(false);

          setCurrentStep((prev) => prev + 1);
          window.scrollTo(0, 0);

          startProcessGeneration(activeTransactionId, totalRows).catch(() => {});
          return;
        } catch {
          return;
        }
      }

      if (currentStep === 2) {
        try {
          const activeTransactionId = await ensureTransaction();
          const totalRows = processGeneration.totalRows || processRows.length || processDisplayRowsOrdered.length || 0;
          setVocabularySkipped(false); // Reset skip flag when navigating normally to vocabulary
          setCurrentStep((prev) => prev + 1);
          window.scrollTo(0, 0);
          startVocabularyGeneration(activeTransactionId, totalRows).catch(() => {});
          return;
        } catch {
          return;
        }
      }

      if (currentStep === 4) {
        try {
          setIsProcessing(true);
          await runVaultApi("Saving Vault content and rebuilding document", async () => {
            const activeTransactionId = await ensureTransaction();
            const rowsToSave = [...comparisonRows].sort((a, b) => (a.rowNumber || 0) - (b.rowNumber || 0));

            if (rowsToSave.length === 0) {
              throw new Error("No comparison content found. Complete Compare and Review first.");
            }

            await saveComparisonRows(activeTransactionId, rowsToSave);
            setComparisonSavedRows(rowsToSave);
            setComparisonSaved(true);
            setComparisonEditMode(false);

            await prepareDownloadArtifact(activeTransactionId, rowsToSave);
            setDownloadReady(true);
          });

          setCurrentStep((prev) => prev + 1);
          window.scrollTo(0, 0);
          return;
        } catch {
          return;
        } finally {
          setIsProcessing(false);
        }
      }

      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      // If on Compare step (4) and vocabulary was skipped, go back to Process (2)
      if (currentStep === 4 && vocabularySkipped) {
        setCurrentStep(2);
        setVocabularySkipped(false);
      } else {
        setCurrentStep(prev => prev - 1);
        // Reset skip flag if navigating to vocabulary step normally
        if (currentStep === 4) {
          setVocabularySkipped(false);
        }
      }
      window.scrollTo(0, 0);
    }
  };

  const handleSkipVocabulary = () => {
    // Skip from Process (2) to Compare (4)
    if (currentStep === 2) {
      setVocabularySkipped(true);
      setCurrentStep(4);
      window.scrollTo(0, 0);
    }
  };

  const isStepValid = () => {
    return isWizardStepValid({
      currentStep,
      clientMappings,
      selectedDocType,
      uploadedFiles,
      processRows,
      processGeneration,
      vocabularyRows,
      vocabularyGeneration,
      downloadReady,
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const openVeevaDocumentation = () => {
    window.open('https://vaultcrmhelp.veeva.com/doc/Content/CRM_topics/Getting_Started/TransitionToVault.htm', '_blank', 'noopener,noreferrer');
  };

  const processRowsOrdered = [...processRows].sort((a, b) => (a.rowNumber || 0) - (b.rowNumber || 0));
  const vocabularyRowsOrdered = [...vocabularyRows].sort((a, b) => (a.rowNumber || 0) - (b.rowNumber || 0));
  const comparisonRowsOrdered = [...comparisonRows].sort((a, b) => (a.rowNumber || 0) - (b.rowNumber || 0));
  const processRowsById = Object.fromEntries(processRows.map((row) => [row.id, row]));
  const vocabularyRowsById = Object.fromEntries(vocabularyRowsOrdered.map((row) => [row.id, row]));
  const comparisonRowsById = Object.fromEntries(comparisonRowsOrdered.map((row) => [row.id, row]));
  const processSourceRowsFromDocument = (documentRows || []).map(toProcessSourceRowFromDocument).sort((a, b) => (a.rowNumber || 0) - (b.rowNumber || 0));
  const processDisplayRowsOrdered = processSourceRowsFromDocument.length > 0
    ? processSourceRowsFromDocument.map((sourceRow) => {
      const generatedRow = processRowsById[sourceRow.id];
      return generatedRow
        ? {
          ...sourceRow,
          moduleId: generatedRow.moduleId || sourceRow.moduleId,
          moduleName: generatedRow.moduleName || sourceRow.moduleName,
          processStatement: generatedRow.processStatement || "",
          vocabularyStatement: generatedRow.vocabularyStatement || generatedRow.processStatement || "",
          finalStatement: generatedRow.finalStatement || generatedRow.vocabularyStatement || generatedRow.processStatement || "",
          processCells: Array.isArray(generatedRow.processCells) ? generatedRow.processCells : [],
          vocabularyCells: Array.isArray(generatedRow.vocabularyCells) ? generatedRow.vocabularyCells : [],
          finalCells: Array.isArray(generatedRow.finalCells) ? generatedRow.finalCells : [],
          edited: generatedRow.edited === true
        }
        : sourceRow;
    })
    : processRowsOrdered;

  const vocabularyDisplayRowsOrdered = processDisplayRowsOrdered.length > 0
    ? processDisplayRowsOrdered.map((sourceRow) => {
      const generatedRow = vocabularyRowsById[sourceRow.id];
      return generatedRow
        ? {
          ...sourceRow,
          processStatement: generatedRow.processStatement || sourceRow.processStatement || "",
          processCells: Array.isArray(generatedRow.processCells) && generatedRow.processCells.length > 0
            ? generatedRow.processCells
            : (Array.isArray(sourceRow.processCells) ? sourceRow.processCells : []),
          vocabularyStatement: generatedRow.vocabularyStatement || "",
          vocabularyCells: Array.isArray(generatedRow.vocabularyCells) ? generatedRow.vocabularyCells : [],
          finalStatement: generatedRow.finalStatement || generatedRow.vocabularyStatement || "",
          finalCells: Array.isArray(generatedRow.finalCells) ? generatedRow.finalCells : [],
          edited: generatedRow.edited === true,
        }
        : {
          ...sourceRow,
          vocabularyStatement: "",
          vocabularyCells: [],
          finalStatement: "",
          finalCells: [],
        };
    })
    : vocabularyRowsOrdered;

  const comparisonDisplayRowsOrdered = processDisplayRowsOrdered.length > 0
    ? processDisplayRowsOrdered.map((sourceRow) => {
      const generatedRow = comparisonRowsById[sourceRow.id];
      return generatedRow
        ? {
          ...sourceRow,
          processStatement: generatedRow.processStatement || sourceRow.processStatement || "",
          processCells: Array.isArray(generatedRow.processCells) && generatedRow.processCells.length > 0
            ? generatedRow.processCells
            : (Array.isArray(sourceRow.processCells) ? sourceRow.processCells : []),
          finalStatement: generatedRow.finalStatement || generatedRow.vocabularyStatement || generatedRow.processStatement || sourceRow.processStatement || "",
          finalCells: Array.isArray(generatedRow.finalCells) && generatedRow.finalCells.length > 0
            ? generatedRow.finalCells
            : (Array.isArray(generatedRow.vocabularyCells) && generatedRow.vocabularyCells.length > 0
              ? generatedRow.vocabularyCells
              : (Array.isArray(generatedRow.processCells) && generatedRow.processCells.length > 0
                ? generatedRow.processCells
                : (Array.isArray(sourceRow.processCells) ? sourceRow.processCells : []))),
          edited: generatedRow.edited === true,
        }
        : {
          ...sourceRow,
          finalStatement: sourceRow.processStatement || "",
          finalCells: Array.isArray(sourceRow.processCells) ? sourceRow.processCells : [],
          edited: false,
        };
    })
    : comparisonRowsOrdered;

  const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
  const hasVocabularyRowChanged = (row) => {
    if (!row) {
      return false;
    }
    if (row.rowKind === "table") {
      const processCells = Array.isArray(row.processCells) ? row.processCells.map((cell) => normalizeText(cell)) : [];
      const vocabularyCells = Array.isArray(row.vocabularyCells) ? row.vocabularyCells.map((cell) => normalizeText(cell)) : [];
      if (vocabularyCells.length === 0) {
        return false;
      }
      if (processCells.length !== vocabularyCells.length) {
        return true;
      }
      return vocabularyCells.some((cell, index) => cell !== (processCells[index] || ""));
    }

    const processText = normalizeText(row.processStatement || "");
    const vocabularyText = normalizeText(row.vocabularyStatement || "");
    if (!vocabularyText) {
      return false;
    }
    return vocabularyText !== processText;
  };

  const vocabularyChangedCount = vocabularyRowsOrdered.reduce(
    (count, row) => count + (hasVocabularyRowChanged(row) ? 1 : 0),
    0,
  );
  const vocabularyUnchangedCount = Math.max(vocabularyRowsOrdered.length - vocabularyChangedCount, 0);

  const renderRowContent = ({
    rowKind,
    text,
    cells,
    stage = "plain",
    originalText = "",
    originalCells = [],
    processText = "",
    processCells = []
  }) => {
    if (rowKind === "table" && Array.isArray(cells) && cells.length > 0) {
      return (
        <div className={`comparison-cell-grid ${stage === "comparison" ? "stage-process-orange" : ""}`}>
          {cells.map((cell, index) => (
            <div key={`${index}-${cell}`} className="comparison-cell-item">
              {stage === "process" && <span className="highlight-process-orange">{cell || ""}</span>}
              {stage === "vocabulary" && renderVocabularyDiffText((Array.isArray(processCells) ? processCells[index] : "") || "", cell || "")}
              {stage === "comparison" && renderComparisonText(
                (Array.isArray(originalCells) ? originalCells[index] : "") || "",
                (Array.isArray(processCells) ? processCells[index] : "") || "",
                cell || ""
              )}
              {stage === "plain" && (cell || "")}
            </div>
          ))}
        </div>
      );
    }

    if (stage === "process") {
      return <span className="process-rich-text highlight-process-orange">{text || ""}</span>;
    }
    if (stage === "vocabulary") {
      return <span className="process-rich-text">{renderVocabularyDiffText(processText || "", text || "")}</span>;
    }
    if (stage === "comparison") {
      return (
        <span className="process-rich-text stage-process-orange">
          {renderComparisonText(originalText || "", processText || "", text || "")}
        </span>
      );
    }

    return (
      <span className="process-rich-text">
        {text || ""}
      </span>
    );
  };

  const buildStructuredBlocks = (rows) => {
    const orderedRows = [...rows];
    const blocks = [];
    let currentTableBlock = null;

    orderedRows.forEach((row, index) => {
      if (row.rowKind === "table") {
        if (!currentTableBlock) {
          currentTableBlock = {
            type: "table",
            key: `table-block-${index}`,
            rows: []
          };
          blocks.push(currentTableBlock);
        }
        currentTableBlock.rows.push(row);
        return;
      }

      currentTableBlock = null;
      blocks.push({
        type: "paragraph",
        key: `paragraph-${row.id}`,
        row
      });
    });

    return blocks;
  };

  const processStructuredBlocks = buildStructuredBlocks(processDisplayRowsOrdered);
  const vocabularyStructuredBlocks = buildStructuredBlocks(vocabularyRowsOrdered);
  const comparisonStructuredBlocks = buildStructuredBlocks(comparisonRowsOrdered);

  const renderDocumentStyleSurface = (rows, keyPrefix, options = {}) => {
    const {
      diffBaseRowsById = null,
      enableDiffHighlight = false,
      diffRenderer = null,
      includeEmptyRows = false,
    } = options;
    const blocks = buildDocumentRenderBlocks(rows, { includeEmptyRows });

    return (
      <div className="document-content-wrapper">
        <div className="document-render-surface">
          <div className="document-render-list">
            {blocks.map((block) => {
              if (block.type === "paragraph") {
                const row = block.row;
                const lineText = String(row.text ?? "");
                if (!includeEmptyRows && !hasVisibleText(lineText)) {
                  return null;
                }

                const baseRow = enableDiffHighlight && diffBaseRowsById
                  ? diffBaseRowsById[row.row_id]
                  : null;
                const baseText = String(baseRow?.text ?? "");
                const renderedText = enableDiffHighlight
                  ? (typeof diffRenderer === "function" ? diffRenderer(baseText, lineText) : renderProcessDiffText(baseText, lineText))
                  : lineText;

                const rowNumber = Number(row.row_number) || 0;
                return (
                  <div
                    key={`${keyPrefix}-${block.key}`}
                    className="document-render-row is-paragraph"
                    data-sync-key={`row-${row.row_id}`}
                    data-sync-kind="row"
                  >
                    <div className="document-render-meta">
                      <span className="document-render-kind">Text</span>
                      <span className="document-render-index">{rowNumber > 0 ? `#${rowNumber}` : "#"}</span>
                    </div>
                    <div className="document-render-text">{hasVisibleText(lineText) ? renderedText : "\u00A0"}</div>
                  </div>
                );
              }

              const normalizedRows = block.rows
                .map((row) => ({
                  row_id: row.row_id,
                  row_number: Number(row.row_number) || 0,
                  cells: getTableCells(row),
                }))
                .filter((row) => includeEmptyRows || row.cells.length > 0);

              if (normalizedRows.length === 0) {
                return null;
              }

              const maxColumns = normalizedRows.reduce((max, row) => Math.max(max, row.cells.length), 1);
              const firstRowNumber = normalizedRows[0].row_number;

              return (
                <div key={`${keyPrefix}-${block.key}`} className="document-render-table-section">
                  <div className="document-render-table-header">
                    <span className="document-render-kind">Table</span>
                    <span className="document-render-index">
                      {firstRowNumber > 0 ? `#${firstRowNumber}` : "#"}
                    </span>
                    <span className="document-render-table-size">{normalizedRows.length} × {maxColumns}</span>
                  </div>
                  <div className="document-render-table-wrap">
                    <table className="document-render-table">
                      <tbody>
                        {normalizedRows.map((row) => {
                          const baseRow = enableDiffHighlight && diffBaseRowsById
                            ? diffBaseRowsById[row.row_id]
                            : null;
                          const baseCellsRaw = baseRow
                            ? getTableCells(baseRow)
                            : [];
                          const cells = Array.from({ length: maxColumns }).map((_, index) =>
                            row.cells[index] !== undefined ? String(row.cells[index]) : ""
                          );

                          return (
                            <tr key={`${keyPrefix}-${row.row_id}`}>
                              {cells.map((cell, index) => {
                                const cellText = String(cell ?? "");
                                const baseCellText = String(baseCellsRaw[index] ?? "");
                                const renderedCell = enableDiffHighlight
                                  ? (typeof diffRenderer === "function" ? diffRenderer(baseCellText, cellText) : renderProcessDiffText(baseCellText, cellText))
                                  : cellText;
                                return (
                                  <td
                                    key={`${keyPrefix}-${row.row_id}-${index}`}
                                    className={hasVisibleText(cellText) ? "" : "document-render-cell-empty"}
                                    data-sync-key={`cell-${row.row_id}-${index}`}
                                    data-sync-kind="cell"
                                  >
                                    {hasVisibleText(cellText) ? renderedCell : "\u00A0"}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderComparisonEditableSurface = (rows, keyPrefix) => {
    const blocks = buildDocumentRenderBlocks(rows);

    return (
      <div className="document-content-wrapper">
        <div className="document-render-surface">
          <div className="document-render-list">
            {blocks.map((block) => {
              if (block.type === "paragraph") {
                const row = block.row;
                const rowId = String(row.row_id || "");
                const lineText = String(row.text ?? "");
                const sourceText = String(processSourceRowsById[rowId]?.text ?? "");
                const processText = String(processRowsById[rowId]?.processStatement ?? "");

                if (!hasVisibleText(lineText) && !comparisonEditMode) {
                  return null;
                }

                const rowNumber = Number(row.row_number) || 0;
                return (
                  <div
                    key={`${keyPrefix}-${block.key}`}
                    className="document-render-row is-paragraph"
                    data-sync-key={`row-${row.row_id}`}
                    data-sync-kind="row"
                  >
                    <div className="document-render-meta">
                      <span className="document-render-kind">Text</span>
                      <span className="document-render-index">{rowNumber > 0 ? `#${rowNumber}` : "#"}</span>
                    </div>
                    <div className="document-render-text">
                      {comparisonEditMode ? (
                        <textarea
                          className="comparison-edit-input"
                          value={lineText}
                          readOnly={!comparisonEditMode}
                          onChange={(event) => handleComparisonTextChange(rowId, event.target.value)}
                        />
                      ) : (
                        renderComparisonText(sourceText, processText, lineText)
                      )}
                    </div>
                  </div>
                );
              }

              const normalizedRows = block.rows
                .map((row) => ({
                  row_id: row.row_id,
                  row_number: Number(row.row_number) || 0,
                  cells: getTableCells(row),
                }))
                .filter((row) => row.cells.length > 0 || comparisonEditMode);

              if (normalizedRows.length === 0) {
                return null;
              }

              const maxColumns = normalizedRows.reduce((max, row) => Math.max(max, row.cells.length), 1);
              const firstRowNumber = normalizedRows[0].row_number;

              return (
                <div key={`${keyPrefix}-${block.key}`} className="document-render-table-section">
                  <div className="document-render-table-header">
                    <span className="document-render-kind">Table</span>
                    <span className="document-render-index">
                      {firstRowNumber > 0 ? `#${firstRowNumber}` : "#"}
                    </span>
                    <span className="document-render-table-size">{normalizedRows.length} × {maxColumns}</span>
                  </div>
                  <div className="document-render-table-wrap">
                    <table className="document-render-table">
                      <tbody>
                        {normalizedRows.map((row) => {
                          const sourceCellsRaw = getTableCells(processSourceRowsById[row.row_id] || {});
                          const processCellsRaw = Array.isArray(processRowsById[row.row_id]?.processCells)
                            ? processRowsById[row.row_id].processCells
                            : [];
                          const cells = Array.from({ length: maxColumns }).map((_, index) =>
                            row.cells[index] !== undefined ? String(row.cells[index]) : ""
                          );

                          return (
                            <tr key={`${keyPrefix}-${row.row_id}`}>
                              {cells.map((cell, index) => {
                                const sourceCell = String(sourceCellsRaw[index] ?? "");
                                const processCell = String(processCellsRaw[index] ?? "");
                                const cellValue = String(cell ?? "");
                                const isEmpty = !hasVisibleText(cellValue);
                                return (
                                  <td
                                    key={`${keyPrefix}-${row.row_id}-${index}`}
                                    className={isEmpty ? "document-render-cell-empty" : ""}
                                    data-sync-key={`cell-${row.row_id}-${index}`}
                                    data-sync-kind="cell"
                                  >
                                    {comparisonEditMode ? (
                                      <textarea
                                        className="comparison-edit-input"
                                        value={cellValue}
                                        readOnly={!comparisonEditMode}
                                        onChange={(event) => handleComparisonCellChange(String(row.row_id || ""), index, event.target.value)}
                                      />
                                    ) : (
                                      isEmpty ? "\u00A0" : renderComparisonText(sourceCell, processCell, cellValue)
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const processSourceRowsForDisplay = (documentRows || []).map((row) => ({
    row_id: row.row_id,
    row_number: Number(row.row_number) || 0,
    row_kind: row.row_kind,
    text: String(row.text ?? ""),
    cells: Array.isArray(row.cells) ? row.cells.map((cell) => String(cell ?? "")) : [],
    structure_ref: row.structure_ref || "",
    style_meta: row.style_meta || {},
  }));

  const processVaultRowsForDisplay = processDisplayRowsOrdered.map((row) => {
    const hasGenerated = Boolean(processRowsById[row.id]);
    const tableCells = Array.isArray(row.processCells) ? row.processCells.map((cell) => String(cell ?? "")) : [];
    const paragraphText = String(row.processStatement ?? "");
    const waitingText = processGeneration.running && !hasGenerated ? "Generating Vault Content..." : "";

    return {
      row_id: row.id,
      row_number: Number(row.rowNumber) || 0,
      row_kind: row.rowKind,
      text: row.rowKind === "table" ? "" : (paragraphText || waitingText),
      cells: row.rowKind === "table"
        ? (tableCells.length > 0 ? tableCells : (waitingText ? [waitingText] : []))
        : [],
      structure_ref: row.sourceRef || "",
      style_meta: {},
    };
  });

  const processSourceRowsById = Object.fromEntries(processSourceRowsForDisplay.map((row) => [row.row_id, row]));

  const vocabularySourceRowsForDisplay = processSourceRowsForDisplay;

  const vocabularyProcessRowsForDisplay = vocabularyDisplayRowsOrdered.map((row) => ({
    row_id: row.id,
    row_number: Number(row.rowNumber) || 0,
    row_kind: row.rowKind,
    text: row.rowKind === "table" ? "" : String(row.processStatement ?? ""),
    cells: row.rowKind === "table"
      ? (Array.isArray(row.processCells) ? row.processCells.map((cell) => String(cell ?? "")) : [])
      : [],
    structure_ref: row.sourceRef || "",
    style_meta: {},
  }));

  const vocabularyVaultRowsForDisplay = vocabularyDisplayRowsOrdered.map((row) => {
    const hasGenerated = Boolean(vocabularyRowsById[row.id]);
    const waitingText = vocabularyGeneration.running && !hasGenerated ? "Generating Vocabulary Content..." : "";
    const tableCells = Array.isArray(row.vocabularyCells) ? row.vocabularyCells.map((cell) => String(cell ?? "")) : [];
    const paragraphText = String(row.vocabularyStatement ?? "");

    return {
      row_id: row.id,
      row_number: Number(row.rowNumber) || 0,
      row_kind: row.rowKind,
      text: row.rowKind === "table" ? "" : (paragraphText || waitingText),
      cells: row.rowKind === "table"
        ? (tableCells.length > 0 ? tableCells : (waitingText ? [waitingText] : []))
        : [],
      structure_ref: row.sourceRef || "",
      style_meta: {},
    };
  });

  const vocabularySourceRowsById = Object.fromEntries(vocabularySourceRowsForDisplay.map((row) => [row.row_id, row]));
  const vocabularyProcessRowsById = Object.fromEntries(vocabularyProcessRowsForDisplay.map((row) => [row.row_id, row]));

  const comparisonSourceRowsForDisplay = processSourceRowsForDisplay;

  const comparisonVaultRowsForDisplay = comparisonDisplayRowsOrdered.map((row) => {
    const tableCells = Array.isArray(row.finalCells) ? row.finalCells.map((cell) => String(cell ?? "")) : [];
    const paragraphText = String(row.finalStatement ?? row.processStatement ?? "");

    return {
      row_id: row.id,
      row_number: Number(row.rowNumber) || 0,
      row_kind: row.rowKind,
      text: row.rowKind === "table" ? "" : paragraphText,
      cells: row.rowKind === "table"
        ? (tableCells.length > 0 ? tableCells : [String(row.finalStatement ?? "")])
        : [],
      structure_ref: row.sourceRef || "",
      style_meta: {},
    };
  });

  const renderStructuredStage = (blocks, mode) => {

    return (
      <div className="process-structured-scroll">
        <div className="process-structured-wrapper">
          {blocks.map((block) => {
            if (block.type === "paragraph") {
              const row = block.row;
              const processRow = processRowsById[row.id] || row;
              const vocabularyRow = vocabularyRowsById[row.id] || row;
              const hasGeneratedProcessRow = Boolean(processRowsById[row.id]);

              const leftParagraph = String(row.veevaStatement ?? "");
              const rightParagraph = mode === "process"
                ? String(row.processStatement ?? (processGeneration.running && !hasGeneratedProcessRow ? "Generating Vault Content..." : ""))
                : mode === "vocabulary"
                  ? String(row.vocabularyStatement ?? "")
                  : String(row.finalStatement ?? "");

              const processBaseline = String(processRow.processStatement ?? "");

              const renderedRight = mode === "process"
                ? renderProcessDiffText(leftParagraph, rightParagraph)
                : mode === "vocabulary"
                  ? renderVocabularyDiffText(leftParagraph, rightParagraph)
                  : renderComparisonText(leftParagraph, processBaseline, rightParagraph);

              return (
                <div key={block.key} className="process-paragraph-block">
                  <div className="process-row-meta">
                    <span className="comparison-ref">#{row.rowNumber}</span>
                  </div>
                  <div className="process-structured-table-wrap">
                    <table className="process-structured-table">
                      {mode !== "process" && (
                        <thead>
                          <tr>
                            <th className="process-head-veeva">Veeva Content - Original</th>
                            <th className="process-head-vault">Vault Content - AI Enhanced</th>
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        <tr>
                          <td>
                            <pre className="process-paragraph-top">{leftParagraph}</pre>
                          </td>
                          <td className="process-vault-cell">
                            {mode === "comparison" && comparisonEditMode ? (
                              <div className="process-paragraph-bottom comparison-paragraph-editable">
                                <textarea
                                  className="comparison-edit-input"
                                  value={rightParagraph}
                                  readOnly={!comparisonEditMode}
                                  onChange={(event) => handleComparisonTextChange(row.id, event.target.value)}
                                />
                              </div>
                            ) : (
                              <pre className="process-paragraph-bottom">{renderedRight}</pre>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            }

            const maxColumns = block.rows.reduce((max, row) => {
              const processRow = processRowsById[row.id] || row;
              const veevaCount = Array.isArray(row.originalCells) ? row.originalCells.length : 0;
              const processCount = Array.isArray(processRow.processCells) ? processRow.processCells.length : 0;
              const vocabularyCount = Array.isArray(row.vocabularyCells) ? row.vocabularyCells.length : 0;
              const finalCount = Array.isArray(row.finalCells) ? row.finalCells.length : 0;
              return Math.max(max, veevaCount, processCount, vocabularyCount, finalCount, 1);
            }, 1);

            return (
              <div key={block.key} className="process-structured-table-wrap">
                <table className="process-structured-table">
                  {mode !== "process" && (
                    <thead>
                      <tr>
                        <th className="process-head-veeva">Veeva Content - Original</th>
                        <th className="process-head-vault">Vault Content - AI Enhanced</th>
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {block.rows.map((row) => {
                      const processRow = processRowsById[row.id] || row;
                      const vocabularyRow = vocabularyRowsById[row.id] || row;
                      const hasGeneratedProcessRow = Boolean(processRowsById[row.id]);

                      const leftCells = Array.isArray(row.originalCells) && row.originalCells.length > 0
                        ? row.originalCells
                        : [row.veevaStatement || ""];

                      const processCells = Array.isArray(processRow.processCells) && processRow.processCells.length > 0
                        ? processRow.processCells
                        : [processRow.processStatement || ""];

                      const rightCells = mode === "process"
                        ? (Array.isArray(row.processCells) && row.processCells.length > 0 ? row.processCells : [row.processStatement || ""])
                        : mode === "vocabulary"
                          ? (Array.isArray(row.vocabularyCells) && row.vocabularyCells.length > 0 ? row.vocabularyCells : [row.vocabularyStatement || ""])
                          : (Array.isArray(row.finalCells) && row.finalCells.length > 0 ? row.finalCells : [row.finalStatement || ""]);

                      return (
                        <React.Fragment key={`${mode}-table-${row.id}`}>
                          <tr className="process-table-row-meta">
                            <td colSpan={2}>
                              <span className="comparison-ref">#{row.rowNumber}</span>
                            </td>
                          </tr>
                          <tr>
                            <td className={leftCells.every((cell) => String(cell ?? "").length === 0) ? "process-structured-empty" : ""}>
                              <div className="process-row-cell-stack">
                                {Array.from({ length: maxColumns }).map((_, cellIndex) => {
                                  const leftValue = String(leftCells[cellIndex] ?? "");
                                  const isLeftEmpty = leftValue.length === 0;
                                  return (
                                    <div key={`left-${row.id}-${cellIndex}`} className={`process-row-cell-line ${isLeftEmpty ? "process-structured-empty" : ""}`}>
                                      <span className="process-row-cell-index">C{cellIndex + 1}</span>
                                      <span className="process-row-cell-value">{isLeftEmpty ? "\u00A0" : leftValue}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                            <td className={`process-vault-cell ${rightCells.every((cell) => String(cell ?? "").length === 0) ? "process-structured-empty" : ""}`}>
                              <div className="process-row-cell-stack">
                                {Array.from({ length: maxColumns }).map((_, cellIndex) => {
                                  const leftValue = String(leftCells[cellIndex] ?? "");
                                  const processBase = String(processCells[cellIndex] ?? "");
                                  const rightValue = String(
                                    rightCells[cellIndex] ?? (
                                      mode === "process" && processGeneration.running && !hasGeneratedProcessRow && cellIndex === 0
                                        ? "Generating Vault Content..."
                                        : ""
                                    )
                                  );
                                  const isRightEmpty = rightValue.length === 0;

                                  const rightRendered = mode === "process"
                                    ? renderProcessDiffText(leftValue, rightValue)
                                    : mode === "vocabulary"
                                      ? renderVocabularyDiffText(leftValue, rightValue)
                                      : renderComparisonText(leftValue, processBase, rightValue);

                                  return (
                                    <div key={`right-${row.id}-${cellIndex}`} className={`process-row-cell-line ${isRightEmpty ? "process-structured-empty" : ""}`}>
                                      <span className="process-row-cell-index">C{cellIndex + 1}</span>
                                      {mode === "comparison" && comparisonEditMode ? (
                                        <textarea
                                          className="comparison-edit-input"
                                          value={rightValue}
                                          readOnly={!comparisonEditMode}
                                          onChange={(event) => handleComparisonCellChange(row.id, cellIndex, event.target.value)}
                                        />
                                      ) : (
                                        <span className="process-row-cell-value">{isRightEmpty ? "\u00A0" : rightRendered}</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Navigation buttons component for reuse
  const NavigationButtons = ({ prependActions = null }) => (
    <WizardNavigationButtons
      prependActions={prependActions}
      currentStep={currentStep}
      totalSteps={steps.length}
      onPrevious={handlePrevious}
      onNext={handleNext}
      canProceed={isStepValid()}
    />
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="wizard-step-content mapping-step">
            <div className="step-header-with-nav">
              <div className="step-header-left">
                <div className="step-icon-wrapper">
                  <FiLink className="step-icon" />
                </div>
                <div className="step-header-text">
                  <h2 className="step-title">Mapping Configuration</h2>
                  <p className="step-subtitle">
                    Configure field mappings between Veeva CRM and Vault CRM.
                  </p>
                </div>
              </div>
              <NavigationButtons />
            </div>

            {/* Table Tabs */}
            <div className="mapping-tabs">
              <button 
                className={`mapping-tab ${activeTable === 'client' ? 'active' : ''}`}
                onClick={() => setActiveTable('client')}
              >
                <FiEdit3 className="tab-icon" />
                Client/Custom Mappings
                <span className="tab-badge">{clientMappings.filter(m => m.enabled).length}</span>
              </button>
              <button 
                className={`mapping-tab ${activeTable === 'veeva' ? 'active' : ''}`}
                onClick={() => setActiveTable('veeva')}
              >
                <FiExternalLink className="tab-icon" />
                Veeva Standard Mappings
                <span className="tab-badge reference">Reference</span>
              </button>
            </div>

            {/* Table 1: Client/Custom Mappings */}
            {activeTable === 'client' && (
              <div className="mapping-table-section">
                <div className="table-header">
                  <div className="table-title-section">
                    <h3 className="table-title">Client/Custom Field Mappings</h3>
                    <p className="table-description">
                      Your organization's custom field mappings. Add, edit, or remove mappings as needed.
                    </p>
                  </div>
                  <div className="table-actions">
                    <button
                      className="upload-mappings-btn"
                      onClick={handleSaveClientMappings}
                      disabled={isMappingLoading || apiInProgress}
                    >
                      {apiInProgress && isApiAction("Saving mappings") ? (
                        <>
                          <FiLoader className="spinner" /> Saving Mappings...
                        </>
                      ) : (
                        <>
                          <FiSave /> Save Mappings
                        </>
                      )}
                    </button>
                    <input
                      type="file"
                      ref={mappingFileInputRef}
                      onChange={handleMappingFileUpload}
                      accept=".csv,.xlsx,.xls"
                      style={{ display: 'none' }}
                    />
                    <button 
                      className="upload-mappings-btn"
                      onClick={() => mappingFileInputRef.current?.click()}
                      disabled={isMappingLoading || apiInProgress}
                    >
                      {apiInProgress && isApiAction("Importing mappings") ? (
                        <>
                          <FiLoader className="spinner" /> Importing Mappings...
                        </>
                      ) : (
                        <>
                          <FiUploadCloud /> Import CSV/Excel
                        </>
                      )}
                    </button>
                    <button
                      className="upload-mappings-btn"
                      onClick={handleRefreshMappingTable}
                      disabled={isMappingLoading || apiInProgress}
                    >
                      {apiInProgress && isApiAction("Refreshing mappings") ? (
                        <>
                          <FiLoader className="spinner" /> Refreshing Mappings...
                        </>
                      ) : (
                        <>
                          <FiRefreshCw /> Refresh Table
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {mappingError && (
                  <div className="reference-note" role="alert">
                    <FiAlertCircle className="note-icon" />
                    <p>{mappingError}</p>
                  </div>
                )}

                {mappingImportSummary && (
                  <div className="source-info">
                    <FiCheckCircle className="source-icon" />
                    <span>
                      Imported {mappingImportSummary.source_file} · read {mappingImportSummary.stats.rows_read},
                      added {mappingImportSummary.stats.rows_inserted}, updated {mappingImportSummary.stats.rows_updated},
                      conflicts {mappingImportSummary.stats.rows_conflicted}, failed {mappingImportSummary.stats.rows_failed}
                    </span>
                  </div>
                )}

                {mappingSaveHint && (
                  <div className="source-info">
                    <FiAlertCircle className="source-icon" />
                    <span>{mappingSaveHint}</span>
                  </div>
                )}

                <div className="mapping-table-wrapper">
                  <table className="mapping-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Veeva CRM Field</th>
                        <th className="arrow-header"></th>
                        <th>Vault CRM Field</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredClientMappings().map((mapping) => (
                        <tr key={mapping.id} className={mapping.enabled ? '' : 'disabled'}>
                          <td>
                            <span className="module-badge">{mapping.module}</span>
                          </td>
                          <td>
                            <code className="field-code veeva">{mapping.veevaCRM}</code>
                          </td>
                          <td className="arrow-cell">
                            <FiArrowRight className="table-arrow" />
                          </td>
                          <td>
                            <code className="field-code vault">{mapping.vaultCRM}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add New Mapping Form */}
                <div className="add-mapping-section">
                  <h4>
                    <FiPlus /> Add New Custom Mapping
                  </h4>
                  <div className="add-mapping-form">
                    <div className="form-field">
                      <label>Module</label>
                      <input
                        type="text"
                        placeholder="e.g., Account, Contact"
                        value={newClientMapping.module}
                        onChange={(e) => setNewClientMapping({ ...newClientMapping, module: e.target.value })}
                      />
                    </div>
                    <div className="form-field">
                      <label>Veeva CRM Field</label>
                      <input
                        type="text"
                        placeholder="e.g., Custom_Field__c"
                        value={newClientMapping.veevaCRM}
                        onChange={(e) => setNewClientMapping({ ...newClientMapping, veevaCRM: e.target.value })}
                      />
                    </div>
                    <div className="form-field arrow-field">
                      <FiArrowRight className="form-arrow" />
                    </div>
                    <div className="form-field">
                      <label>Vault CRM Field</label>
                      <input
                        type="text"
                        placeholder="e.g., custom_field__v"
                        value={newClientMapping.vaultCRM}
                        onChange={(e) => setNewClientMapping({ ...newClientMapping, vaultCRM: e.target.value })}
                      />
                    </div>
                    <button 
                      className="add-btn"
                      onClick={handleAddClientMapping}
                      disabled={!newClientMapping.module || !newClientMapping.veevaCRM || !newClientMapping.vaultCRM}
                    >
                      <FiPlus /> Add Mapping
                    </button>
                  </div>
                </div>

                <div className="mapping-summary">
                  <div className="summary-stat">
                    <span className="stat-value">{clientMappings.length}</span>
                    <span className="stat-label">Total Mappings</span>
                  </div>
                  <div className="summary-stat enabled">
                    <span className="stat-value">{clientMappings.filter(m => m.enabled).length}</span>
                    <span className="stat-label">Enabled</span>
                  </div>
                  <div className="summary-stat disabled">
                    <span className="stat-value">{clientMappings.filter(m => !m.enabled).length}</span>
                    <span className="stat-label">Disabled</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-value">{clientMappings.filter(m => m.status === "needs_review").length}</span>
                    <span className="stat-label">Needs Review</span>
                  </div>
                </div>
              </div>
            )}

            {/* Table 2: Veeva Standard Mappings (Reference Only) */}
            {activeTable === 'veeva' && (
              <div className="mapping-table-section reference-section">
                <div className="table-header">
                  <div className="table-title-section">
                    <h3 className="table-title">
                      Veeva Standard Context
                      <span className="reference-badge">
                        <FiAlertCircle /> Reference Only
                      </span>
                    </h3>
                    <p className="table-description">
                      Context extracted from Veeva transition documentation and indexed separately for mapping reference.
                    </p>
                  </div>
                  <div className="table-actions">
                    <div className="filter-dropdown">
                      <label>Filter by Group:</label>
                      <select 
                        value={filterModule} 
                        onChange={(e) => setFilterModule(e.target.value)}
                      >
                        <option value="all">All Groups</option>
                        {getUniqueModules().map(mod => (
                          <option key={mod} value={mod}>{mod}</option>
                        ))}
                      </select>
                    </div>
                    <button 
                      className="view-docs-btn"
                      onClick={openVeevaDocumentation}
                    >
                      <FiExternalLink /> View Veeva Docs
                    </button>
                  </div>
                </div>

                <div className="source-info">
                  <FiAlertCircle className="source-icon" />
                  <span>
                    Source: <a href="https://vaultcrmhelp.veeva.com/doc/Content/CRM_topics/Getting_Started/TransitionToVault.htm" target="_blank" rel="noopener noreferrer">
                      Veeva Vault CRM Transition Documentation
                    </a>
                  </span>
                </div>

                <div className="mapping-table-wrapper">
                  <table className="mapping-table reference-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Snippet</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredVeevaMappings().map((mapping) => (
                        <tr key={mapping.context_id}>
                          <td>{mapping.title}</td>
                          <td className="description-cell">
                            {mapping.snippet}
                          </td>
                          <td>
                            <a href={mapping.source_url} target="_blank" rel="noopener noreferrer">
                              {mapping.title || "View Reference"}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="reference-note">
                  <FiAlertCircle className="note-icon" />
                  <p>
                    <strong>Note:</strong> This table displays standard Veeva field mappings for reference purposes only. 
                    These mappings cannot be edited. To add custom mappings for your organization, switch to the 
                    "Client/Custom Mappings" tab.
                  </p>
                </div>

                <div className="mapping-summary reference-summary">
                  <div className="summary-stat">
                    <span className="stat-value">{getFilteredVeevaMappings().length}</span>
                    <span className="stat-label">
                      {filterModule === 'all' ? 'Total Context Rows' : `${filterModule} Rows`}
                    </span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-value">{getUniqueModules().length}</span>
                    <span className="stat-label">Groups Covered</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 1:
        return (
          <DocumentsStep
            NavigationButtons={NavigationButtons}
            selectedDocType={selectedDocType}
            setSelectedDocType={setSelectedDocType}
            documentTypes={documentTypes}
            fileInputRef={fileInputRef}
            handleFileUpload={handleFileUpload}
            uploadedFiles={uploadedFiles}
            formatFileSize={formatFileSize}
            handleRemoveFile={handleRemoveFile}
            handleRefreshDocumentContent={handleRefreshDocumentContent}
            handleClearDocumentContent={handleClearDocumentContent}
            apiInProgress={apiInProgress}
            showDocumentContent={showDocumentContent}
            documentRows={documentRows}
            renderDocumentStyleSurface={renderDocumentStyleSurface}
          />
        );

      case 2:
        return (
          <ProcessStep
            NavigationButtons={NavigationButtons}
            processGeneration={processGeneration}
            processDisplayRowsOrdered={processDisplayRowsOrdered}
            processRows={processRows}
            processSourceRowsForDisplay={processSourceRowsForDisplay}
            processVaultRowsForDisplay={processVaultRowsForDisplay}
            processSourceRowsById={processSourceRowsById}
            renderDocumentStyleSurface={renderDocumentStyleSurface}
            onSkipVocabulary={handleSkipVocabulary}
          />
        );

      case 3:
        return (
          <VocabularyStep
            NavigationButtons={NavigationButtons}
            vocabularyGeneration={vocabularyGeneration}
            vocabularyDisplayRowsOrdered={vocabularyDisplayRowsOrdered}
            vocabularyRows={vocabularyRows}
            vocabularyChangedCount={vocabularyChangedCount}
            vocabularyUnchangedCount={vocabularyUnchangedCount}
            vocabularySourceRowsForDisplay={vocabularySourceRowsForDisplay}
            vocabularyVaultRowsForDisplay={vocabularyVaultRowsForDisplay}
            vocabularySourceRowsById={vocabularySourceRowsById}
            renderDocumentStyleSurface={renderDocumentStyleSurface}
            renderVocabularyDiffText={renderVocabularyDiffText}
          />
        );

      case 4:
        return (
          <ComparisonStep
            NavigationButtons={NavigationButtons}
            setComparisonEditMode={setComparisonEditMode}
            comparisonEditMode={comparisonEditMode}
            apiInProgress={apiInProgress}
            comparisonRows={comparisonRows}
            handleSaveComparisonEdits={handleSaveComparisonEdits}
            comparisonDisplayRowsOrdered={comparisonDisplayRowsOrdered}
            comparisonSourceRowsForDisplay={comparisonSourceRowsForDisplay}
            comparisonVaultRowsForDisplay={comparisonVaultRowsForDisplay}
            renderDocumentStyleSurface={renderDocumentStyleSurface}
            renderComparisonEditableSurface={renderComparisonEditableSurface}
            comparisonSaved={comparisonSaved}
          />
        );

      case 5:
        return (
          <DownloadStep
            NavigationButtons={NavigationButtons}
            downloadReady={downloadReady}
            documentTypes={documentTypes}
            selectedDocType={selectedDocType}
            uploadedFiles={uploadedFiles}
            processRows={processRows}
            vocabularyChanges={vocabularyChanges}
            clientMappings={clientMappings}
            handlePrepareDownload={handlePrepareDownload}
            isProcessing={isProcessing}
            handleDownload={handleDownload}
            onComplete={onComplete}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="migration-wizard">
      <div className="wizard-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
      </div>
      
      <div className="wizard-header">
        <button className="back-button wizard-back" onClick={onBack}>
          <FiArrowLeft className="back-icon" />
          <span>Back to Overview</span>
        </button>
        <h1 className="wizard-title">Migration Wizard</h1>
        <div className="wizard-header-spacer"></div>
      </div>
      
      <div className="wizard-progress">
        <div className="wizard-progress-inner">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;
            return (
              <React.Fragment key={step.id}>
                <div className={`progress-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                  <div className="progress-step-indicator">
                    {isCompleted ? (
                      <FiCheck className="progress-check" />
                    ) : (
                      <StepIcon className="progress-icon" />
                    )}
                  </div>
                  <span className="progress-step-title">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`progress-connector ${isCompleted ? 'completed' : ''}`}></div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      
      <div className="wizard-content">
        {renderStepContent()}
      </div>

      {apiInProgress && (
        <div className="vault-api-loader-overlay" role="status" aria-live="polite" aria-label="Loading">
          <div className="vault-api-loader-card">
            <span className="vault-api-loader-spinner spinner" />
            <span className="vault-api-loader-text">{apiActionLabel || "Processing Request..."}</span>
          </div>
        </div>
      )}

      {apiPopup.open && (
        <div className="vault-api-popup-overlay" role="alertdialog" aria-modal="true" aria-label="Unable to process">
          <div className="vault-api-popup">
            <div className="vault-api-popup-header">
              <div className="vault-api-popup-title-wrap">
                <FiAlertCircle className="vault-api-popup-icon" />
                <h3 className="vault-api-popup-title">Unable to process</h3>
              </div>
              <button
                className="vault-api-popup-close"
                onClick={() => setApiPopup({ open: false, message: "" })}
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>
            <p className="vault-api-popup-message">{apiPopup.message || "Request failed"}</p>
            <div className="vault-api-popup-actions">
              <button
                className="vault-api-popup-action-btn"
                onClick={() => setApiPopup({ open: false, message: "" })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MigrationWizard;